import React, { useState, useRef, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { hapticLight, hapticMedium } from '@/utils/haptics';

/**
 * Pull-to-Refresh Component
 * Native-style pull down gesture to refresh content
 * 
 * Usage:
 * <PullToRefresh onRefresh={async () => await fetchData()}>
 *   <YourContent />
 * </PullToRefresh>
 */
const PullToRefresh = ({ 
  children, 
  onRefresh, 
  threshold = 80,
  maxPull = 120,
  disabled = false 
}) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  
  const containerRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);

  const handleTouchStart = useCallback((e) => {
    if (disabled || isRefreshing) return;
    
    const container = containerRef.current;
    if (!container) return;
    
    // Only enable pull-to-refresh when scrolled to top
    if (container.scrollTop > 0) return;
    
    startY.current = e.touches[0].clientY;
    setIsPulling(true);
  }, [disabled, isRefreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || disabled || isRefreshing) return;
    
    const container = containerRef.current;
    if (!container || container.scrollTop > 0) {
      setIsPulling(false);
      setPullDistance(0);
      return;
    }
    
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;
    
    if (diff > 0) {
      // Apply resistance - pull distance decreases as you pull further
      const resistance = 0.5;
      const adjustedDiff = Math.min(diff * resistance, maxPull);
      setPullDistance(adjustedDiff);
      
      // Haptic feedback when crossing threshold
      if (adjustedDiff >= threshold && pullDistance < threshold) {
        hapticLight();
      }
      
      // Prevent default scroll when pulling
      e.preventDefault();
    }
  }, [isPulling, disabled, isRefreshing, threshold, maxPull, pullDistance]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling || disabled) return;
    
    setIsPulling(false);
    
    if (pullDistance >= threshold && onRefresh) {
      setIsRefreshing(true);
      hapticMedium();
      
      try {
        await onRefresh();
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [isPulling, disabled, pullDistance, threshold, onRefresh]);

  // Calculate visual states
  const progress = Math.min(pullDistance / threshold, 1);
  const showIndicator = pullDistance > 10 || isRefreshing;
  const isReady = pullDistance >= threshold;

  return (
    <div 
      ref={containerRef}
      className="h-full overflow-y-auto overflow-x-hidden scrollbar-hide"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ 
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'none'
      }}
    >
      {/* Pull Indicator */}
      <div 
        className="flex items-center justify-center transition-all duration-200 overflow-hidden"
        style={{ 
          height: showIndicator ? Math.max(pullDistance, isRefreshing ? 50 : 0) : 0,
          opacity: showIndicator ? 1 : 0
        }}
      >
        <div 
          className={`flex items-center gap-2 ${isReady || isRefreshing ? 'text-[#5B3DF5]' : 'text-slate-400'}`}
          style={{
            transform: `rotate(${isRefreshing ? 0 : progress * 180}deg)`,
            transition: isRefreshing ? 'none' : 'transform 0.1s ease'
          }}
        >
          <RefreshCw 
            size={20} 
            className={isRefreshing ? 'animate-spin' : ''} 
          />
        </div>
        {!isRefreshing && (
          <span className={`text-xs ml-2 ${isReady ? 'text-[#5B3DF5] font-medium' : 'text-slate-400'}`}>
            {isReady ? 'Release to refresh' : 'Pull to refresh'}
          </span>
        )}
        {isRefreshing && (
          <span className="text-xs ml-2 text-[#5B3DF5] font-medium">
            Refreshing...
          </span>
        )}
      </div>
      
      {/* Content with pull transform */}
      <div 
        style={{ 
          transform: isPulling && !isRefreshing ? `translateY(${pullDistance * 0.3}px)` : 'translateY(0)',
          transition: isPulling ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
